// src/pages/ia-paca/metiers/architecte-ia.tsx
//
// Page métier — Architecte IA en PACA.

import MetierPageTemplate, {
  type MetierPageConfig,
} from '../../../components/ia-paca/MetierPageTemplate';
import { Layers, Cpu, Cloud, ShieldCheck, GitBranch, Brain } from 'lucide-react';

const CONFIG: MetierPageConfig = {
  slug: 'architecte-ia',
  metaTitle: 'Recrutement Architecte IA en PACA — Marseille, Sophia | recruteurs.io',
  metaDescription:
    "Recruter un Architecte IA en PACA : profils seniors capables de concevoir l'architecture cible IA d'une entreprise (data plane, MLOps, plateformes LLM, gouvernance). Salaire 95-160k€.",
  metaKeywords:
    'recrutement Architecte IA, Architecte IA Marseille, Architecte IA Sophia, salaire Architecte IA 2026, recruter Architecte intelligence artificielle PACA',
  heroLabel: 'Architecte IA',
  heroSubtitle:
    "Le profil senior qui pose les fondations IA d'une entreprise pour les 5 prochaines années. Choisit les briques, pose les patterns, structure les équipes. Ne code pas (ou très peu) mais sait tout faire — sinon il ne peut pas trancher.",
  heroBadges: ['Data plane', 'Plateforme ML', 'LLM Ops', 'Gouvernance'],
  jsonLdName: 'Architecte IA',
  jsonLdDescription:
    "L'Architecte IA conçoit l'architecture cible des systèmes IA d'une entreprise : data plane, plateforme MLOps, LLM platform, gouvernance, sécurité. Vision long terme + capacité à orchestrer plusieurs équipes.",
  jsonLdSkills:
    'Cloud architecture (AWS / GCP / Azure), data architecture, MLOps platforms, LLM platforms (RAG at scale, multi-tenant), gouvernance IA, AI Act, sécurité, FinOps, équipe scaling, conduite du changement',
  jsonLdMinSalary: 95000,
  jsonLdMedianSalary: 125000,
  jsonLdMaxSalary: 160000,
  jsonLdEducation: 'Ingénieur grande école avec 10+ ans d\'expérience tech.',
  jsonLdExperience: 'Senior obligatoire (8+ ans). Idéalement passé par 2 contextes (start-up + corporate).',
  intro: {
    title: "Architecte IA : la vision long terme + l'autorité pour la défendre.",
    paragraphs: [
      "L'Architecte IA est l'équivalent IA d'un Architecte Solution senior. Mais avec une spécificité : l'IA évolue trop vite pour copier un pattern d'il y a 18 mois. Il faut <strong>une vision long terme + une veille permanente + l'humilité de remettre en cause sa propre architecture</strong>.",
      "Sur une journée type : il anime des design reviews, arbitre entre 2 stacks (Bedrock vs Vertex AI vs maison), définit les zones de responsabilité (où s'arrête le Data Eng, où commence le ML Eng), instruit les comités IA, et passe du temps avec les équipes pour vérifier que les choix d'archi tiennent en prod.",
      "<strong>Pourquoi c'est cher (95-160 k€) :</strong> il y a peu d'Architectes IA en PACA. La plupart se sont formés sur le tas pendant 5-10 ans dans des contextes très variés (scale-up + ETI + corporate). Ces profils sont chassés en permanence par les sociétés de conseil et les grands groupes. Si vous voulez en attirer un, calibrez votre poste avec une mission claire et un mandat fort.",
    ],
  },
  stackTitle: 'Les domaines que votre Architecte IA doit couvrir.',
  stack: [
    {
      Icon: Cloud,
      title: 'Cloud & infra',
      tools: ['AWS / GCP / Azure', 'Kubernetes (avancé)', 'Terraform', 'Service mesh (Istio, Linkerd)', 'Multi-region'],
    },
    {
      Icon: Layers,
      title: 'Data architecture',
      tools: ['Lakehouse (Databricks, Iceberg)', 'Snowflake / BigQuery', 'Streaming (Kafka, Flink)', 'CDC', 'Data contracts'],
    },
    {
      Icon: Cpu,
      title: 'Plateforme ML',
      tools: ['SageMaker / Vertex AI / Azure ML', 'Kubeflow', 'MLflow + W&B', 'Feature store (Feast)', 'Model registry'],
    },
    {
      Icon: Brain,
      title: 'Plateforme LLM',
      tools: ['Bedrock / Vertex AI Gen', 'Self-hosted (vLLM, TGI)', 'Vector DB enterprise (Pinecone, Weaviate)', 'Gateway LLM (Portkey, Helicone)', 'Multi-tenant'],
    },
    {
      Icon: ShieldCheck,
      title: 'Gouvernance & sécurité',
      tools: ['AI Act', 'RGPD', 'Model risk management', 'ACL multi-tenant', 'Audit & lineage', 'Red teaming'],
    },
    {
      Icon: GitBranch,
      title: 'Org & gouvernance produit',
      tools: ['Team topology (stream / platform / enabling)', 'Comité IA', 'FinOps IA', 'Process design review', 'Roadmap & priorisation'],
    },
  ],
  salaryByCityTitle: '95-160 k€, premium fort sur expérience scale-out (PB de données / 100+ modèles en prod).',
  salaryByCity: [
    { ville: 'Marseille', confirme: '95 – 120 k€', senior: '120 – 145 k€', expert: '140 – 165 k€' },
    { ville: 'Aix-en-Provence', confirme: '92 – 115 k€', senior: '115 – 140 k€', expert: '135 – 160 k€' },
    { ville: 'Sophia Antipolis', confirme: '100 – 130 k€', senior: '130 – 155 k€', expert: '150 – 180 k€' },
    { ville: 'Nice', confirme: '95 – 122 k€', senior: '122 – 148 k€', expert: '145 – 170 k€' },
  ],
  questionsTitle: '5 questions pour qualifier un Architecte IA.',
  questionsIntro:
    "Ces questions séparent un Architecte qui a réellement mis en production des plateformes IA d'un Architecte d'avant-projet.",
  questions: [
    {
      q: "Décris une architecture IA cible que tu as conçue et mise en œuvre. Combien de temps ça a pris et quels ont été les compromis ?",
      good:
        "Cite un cas concret avec : contexte initial, vision cible, séquence des étapes (data plane d'abord ? MLOps ? LLM platform ?), équipes mobilisées, compromis assumés (build vs buy, scale vs simplicité), durée réelle (souvent 18-36 mois pour une cible complète).",
      bad: "Reste en théorie ou cite uniquement des slides. Pas de notion de compromis, pas de chiffres concrets de durée et coût.",
    },
    {
      q: "Bedrock vs Vertex AI vs vLLM self-hosted vs Mistral on-prem : comment tu choisis pour un client B2B financier ?",
      good:
        "Décortique selon : sensibilité des données (souveraineté → Mistral on-prem ou Bedrock EU), coût total (1k req/mois ≠ 100k), latence requise, capacité d'équipe à opérer self-hosted, contraintes RH (équipe MLOps dispo ?). Recommande souvent un mix selon le cas d'usage.",
      bad: "'Vertex AI c'est le meilleur.' Ou 'on hoste tout en interne'. Pas de cadre de décision, pas de prise en compte des contraintes business.",
    },
    {
      q: 'Comment tu structures une équipe IA cible pour une ETI de 500 personnes qui démarre ?',
      good:
        "Team topology mature : 1 Lead IA, 2-3 Data/ML Engineers (selon backlog), 1 MLOps, 1-2 Data Scientists, 1 AI Engineer LLM. Discussion sur stream-aligned vs platform team selon maturité. Cadre la gouvernance (comité IA mensuel, eval committee). Sait sizer un budget annuel.",
      bad: "Donne un effectif sans logique team topology. Confond Data Scientist et ML Engineer. Pas de notion gouvernance.",
    },
    {
      q: "Comment tu intègres l'AI Act dans tes choix d'architecture ?",
      good:
        "Identifie en design review les systèmes potentiellement à haut risque (recrutement, scoring, santé). Pose des contraintes architecturales (auditabilité, traçabilité, contrôle humain, kill switch). Structure le lineage data + model + prompt pour pouvoir prouver la conformité. Sait que c'est un avantage compétitif.",
      bad: "'On verra à la mise en conformité plus tard.' Risque pour le client, dette d'architecture.",
    },
    {
      q: "Tu hérites d'une plateforme ML qui coûte 80k€/mois pour 5 modèles en prod. Comment tu attaques le sujet FinOps ?",
      good:
        "Méthodique : 1) attribution des coûts par modèle / par équipe (chargeback / showback), 2) profiling (compute, storage, network, LLM tokens), 3) optimisations classiques (batching, caching sémantique, modèles plus petits, spot instances GPU), 4) revue des modèles peu utilisés, 5) plan de réduction sur 6 mois avec target.",
      bad: "'On réduit la taille des instances.' Pas de mesure, pas de méthodo, throw away revenue pour économiser.",
    },
  ],
  ctaTitle: "Vous cherchez un Architecte IA en PACA ?",
  ctaSubtitle:
    "Profil rare, mission longue (souvent 3-5 ans). Notre court-list cible 3-5 profils en 25-40 jours, avec un alignement fort sur votre maturité IA actuelle.",
};

export default function ArchitecteIaPage() {
  return <MetierPageTemplate config={CONFIG} />;
}
